'use client';

import { Typography } from '../../../components/typography/Typography';
import { type Component, type ComponentProps } from '../../../types/component';
import { tv } from '../../../utils/tv';

const formDescriptionClasses = {
  root: 'zds-form-description-root',
} as const;

const formDescriptionStyles = tv({
  base: [
    formDescriptionClasses.root,
    'block',
    'z--form-description-margin-top z--form-description-padding-start',
  ],
  variants: {
    color: {
      primary: 'text-secondary',
      white: 'text-static-white-main',
    },
    isSuccess: {
      true: 'text-success-main',
    },
    isInvalid: {
      true: 'text-error-main',
    },
    isDisabled: {
      true: 'disabled-foreground',
    },
  },
});

interface FormDescriptionBaseProps {
  /**
   * The colour scheme of the description text.
   * @default 'primary'
   */
  color?: 'primary' | 'white';
  /** Default helper text displayed below the field. */
  description?: string;
  /** Error message shown when `isInvalid` is true. Overrides `description`. */
  errorMessage?: string;
  /** Success message shown when `isSuccess` is true. Overrides `description`. */
  successMessage?: string;
  /** Whether the field is in an invalid/error state. */
  isInvalid?: boolean;
  /** Whether the field is in a success state. */
  isSuccess?: boolean;
  /** Whether the field is disabled. */
  isDisabled?: boolean;
}

type FormDescriptionProps = ComponentProps<FormDescriptionBaseProps, 'span'>;

type FormDescriptionComponent = Component<FormDescriptionBaseProps, 'span'>;

const FormDescription: FormDescriptionComponent = (props) => {
  const {
    color = 'primary',
    description,
    errorMessage,
    successMessage,
    isInvalid,
    isSuccess,
    isDisabled,
    className,
    ref,
    ...rest
  } = props;

  // Resolve which text to display
  const text =
    isInvalid && errorMessage
      ? errorMessage
      : isSuccess && successMessage
        ? successMessage
        : description;

  if (!text) {
    return null;
  }

  return (
    <Typography
      component="span"
      variant="caption"
      {...rest}
      ref={ref}
      color="inherit"
      aria-disabled={isDisabled || undefined}
      className={formDescriptionStyles({
        color,
        isSuccess,
        isInvalid,
        isDisabled,
        className,
      })}
    >
      {text}
    </Typography>
  );
};

FormDescription.displayName = 'FormDescription';

export {
  FormDescription,
  type FormDescriptionProps,
  type FormDescriptionBaseProps,
  formDescriptionClasses,
};
